import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { backofficeApi } from '@/lib/backoffice-api'
import type {
  Paginated,
  BackofficeUserListItem,
  PasswordResetTokenResult,
  UserRole,
} from '@/types/backoffice'

export interface ListBackofficeUsersParams {
  search?: string
  tenantId?: string
  role?: UserRole
  active?: boolean
  page?: number
  perPage?: number
}

const KEYS = {
  list: (p: ListBackofficeUsersParams) => ['backoffice', 'users', p] as const,
  detail: (id: string) => ['backoffice', 'users', id] as const,
}

export function useBackofficeUsers(params: ListBackofficeUsersParams = {}) {
  return useQuery({
    queryKey: KEYS.list(params),
    queryFn: () =>
      backofficeApi
        .get<Paginated<BackofficeUserListItem>>('/backoffice/users', { params })
        .then((r) => r.data),
  })
}

export function useBackofficeUser(id: string) {
  return useQuery({
    queryKey: KEYS.detail(id),
    queryFn: () =>
      backofficeApi.get<BackofficeUserListItem>(`/backoffice/users/${id}`).then((r) => r.data),
    enabled: !!id,
  })
}

export function useUpdateUserRole(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (role: UserRole) =>
      backofficeApi
        .patch<BackofficeUserListItem>(`/backoffice/users/${id}/role`, { role })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backoffice', 'users'] }),
  })
}

export function useSetUserActive() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      backofficeApi
        .patch<BackofficeUserListItem>(`/backoffice/users/${id}/${active ? 'activate' : 'deactivate'}`)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['backoffice', 'users'] }),
  })
}

export function useGeneratePasswordReset() {
  return useMutation({
    mutationFn: (id: string) =>
      backofficeApi
        .post<PasswordResetTokenResult>(`/backoffice/users/${id}/password-reset`)
        .then((r) => r.data),
  })
}
